import { v4 as uuidv4 } from 'uuid';

/**
 * Contexto de campaña de un visitante: de qué enlace llegó y con qué clic.
 *
 * Antes esto vivía repartido: `captureUTMParams()` leía la URL una única vez al
 * construirse el servicio de analítica y dejaba los UTM en sessionStorage, y el
 * primer pageview los consumía y los borraba. Eso tenía tres agujeros:
 *
 *  1. Los UTM viajaban en UN solo golpe. Los eventos salen por colas distintas
 *     de RabbitMQ, sin garantía de orden, así que si un `commerce` llegaba
 *     antes que ese pageview la sesión se creaba sin campaña y se perdía para
 *     siempre. Ahora la campaña viaja en todos los golpes y el backend solo
 *     rellena lo que esté vacío, con lo que el orden deja de importar.
 *  2. sessionStorage muere al cerrar la pestaña: quien entraba por Instagram y
 *     compraba al día siguiente no se atribuía a nadie. Ahora vive en
 *     localStorage con una ventana de 30 días.
 *  3. No había forma de distinguir dos clics distintos a la misma campaña. El
 *     `click_id` identifica la visita concreta y viaja también en la URL (`ti`)
 *     para sobrevivir a que alguien comparta o recargue el enlace.
 *
 * La atribución es de PRIMER contacto: un contexto vigente no se pisa salvo que
 * llegue un `utm_source` distinto, que es un clic a otra campaña.
 */

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/** Nombre corto a propósito: va en URLs que se comparten. */
export const CLICK_ID_PARAM = 'ti';

const STORAGE_KEY = 'analytics_campaign';

/** Ventana de atribución, en milisegundos (30 días). */
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const nuevoClickId = () => uuidv4().replace(/-/g, '').slice(0, 12);

const leerAlmacen = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const escribirAlmacen = ctx => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* modo privado o almacenamiento lleno: la analítica nunca rompe la tienda */
  }
};

const vigente = (ctx, ahora) =>
  !!ctx && typeof ctx.ts === 'number' && ahora - ctx.ts < ATTRIBUTION_TTL_MS;

/**
 * Lee los parámetros de campaña de una query string.
 * @returns {{utm: object, clickId: string|null, hayUTM: boolean}}
 */
export const parseParams = (search = '') => {
  const params = new URLSearchParams(search);
  const utm = {};
  let hayUTM = false;
  UTM_KEYS.forEach(key => {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
      hayUTM = true;
    }
  });
  return { utm, clickId: params.get(CLICK_ID_PARAM) || null, hayUTM };
};

/**
 * Registra la llegada: si la URL trae campaña, la guarda (o conserva la que ya
 * había, si es la misma). Devuelve el contexto vigente o null.
 *
 * @param {string} search query string de la que leer (por defecto, la actual)
 * @param {number} ahora  instante de referencia, inyectable para los tests
 */
export const capture = (search = window.location.search, ahora = Date.now()) => {
  const { utm, clickId, hayUTM } = parseParams(search);
  const guardado = leerAlmacen();
  const previo = vigente(guardado, ahora) ? guardado : null;

  if (!hayUTM) {
    // Sin UTM en la URL no se abre campaña nueva. Un `ti` suelto solo sirve
    // para recuperar el clic cuando alguien comparte un enlace ya navegado.
    if (previo) return previo;
    if (!clickId) return null;
    const soloClic = { click_id: clickId, ts: ahora };
    escribirAlmacen(soloClic);
    return soloClic;
  }

  // Primer contacto: la misma campaña no se vuelve a abrir, para no perder el
  // click_id original ni reiniciar la ventana de atribución.
  if (previo && previo.utm_source === utm.utm_source) return previo;

  const ctx = { ...utm, click_id: clickId || nuevoClickId(), ts: ahora };
  escribirAlmacen(ctx);
  return ctx;
};

/** Contexto vigente, o null si no hay campaña o ya expiró. */
export const get = (ahora = Date.now()) => {
  const ctx = leerAlmacen();
  return vigente(ctx, ahora) ? ctx : null;
};

/**
 * Pares [clave, valor] que deben viajar en la URL. `ts` es de uso interno y no
 * sale nunca.
 */
export const toParams = ctx => {
  if (!ctx) return [];
  const pares = UTM_KEYS.filter(k => ctx[k]).map(k => [k, ctx[k]]);
  if (ctx.click_id) pares.push([CLICK_ID_PARAM, ctx.click_id]);
  return pares;
};

/** Campos que se adjuntan a cada golpe de analítica. */
export const toPayload = ctx => {
  if (!ctx) return {};
  const payload = {};
  UTM_KEYS.forEach(k => {
    if (ctx[k]) payload[k] = ctx[k];
  });
  if (ctx.click_id) payload.click_id = ctx.click_id;
  return payload;
};

export const clear = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignorado a propósito */
  }
};

const campaignContext = { capture, get, toParams, toPayload, clear, parseParams };
export default campaignContext;
